/**
 * Plants Tab Content
 * Product browser with search and placement modes for plant placement.
 */

import './PlantsTabContent.css'
import type { TabContentRenderer } from './LeftPanel'
import { LAYER_CONFIG } from './LeftPanel'
import { ProductBrowser } from './ProductBrowser'
import { getViewer, getSelectedProduct, getPlacementMode } from './state'
import { startWalkthrough } from './Walkthrough'
import { t } from '@/i18n'

export class PlantsTabContent implements TabContentRenderer {
  private container: HTMLElement | null = null
  private productBrowser: ProductBrowser

  constructor() {
    this.productBrowser = new ProductBrowser({
      layer: 'plants',
      accentColor: LAYER_CONFIG.plants.color,
      showSearch: true,
      searchPlaceholder: t('plants.searchPlaceholder', 'Search plants...'),
      onProductSelected: (product) => {
        const viewer = getViewer()
        const toolManager = viewer?.getToolManager()
        if (product && toolManager) {
          // Preload GLB model so it's ready when user clicks to place
          viewer?.preloadProductModel(product.id)
          viewer?.ensureProductCached(product)
          toolManager.setActiveTool('place-object')
        } else if (!product && toolManager) {
          toolManager.setActiveTool('select')
        }
        this.updateHint()
      },
    })
  }

  render(container: HTMLElement): void {
    this.container = container
    this.renderContent()
  }

  destroy(): void {
    this.productBrowser.destroy()
    this.container = null
  }

  onActivate(): void {
    this.updateHint()
  }

  onDeactivate(): void {
    // nothing
  }

  private renderContent(): void {
    if (!this.container) return

    this.container.innerHTML = `
      <div class="plants-tab-content">
        <!-- Product Browser -->
        <div class="plants-section-title">
          <span>${t('plants.catalog', 'Plant Catalog')}</span>
          <button class="tab-help-btn" data-walkthrough="plants" title="${t('tooltip.help', 'Help')}">
            <i class="fa-solid fa-circle-question"></i>
          </button>
        </div>
        <div class="plants-product-browser"></div>

        <!-- Hint Bar -->
        <div class="plants-hint-bar">
          <i class="fa-solid fa-circle-info"></i>
          <span class="plants-hint-text">${this.getHintText()}</span>
        </div>
      </div>
    `

    // Render product browser
    const browserContainer = this.container.querySelector('.plants-product-browser') as HTMLElement
    if (browserContainer) {
      this.productBrowser.render(browserContainer)
    }

    // Wire help button
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('plants'))
  }

  private updateHint(): void {
    if (!this.container) return
    const hintEl = this.container.querySelector('.plants-hint-text')
    if (hintEl) {
      hintEl.textContent = this.getHintText()
    }
  }

  private getHintText(): string {
    const selectedProduct = getSelectedProduct()
    const mode = getPlacementMode()
    if (selectedProduct) {
      if (mode === 'continuous') {
        return t('plants.hintContinuous', `Click to place ${selectedProduct.name} (Esc to stop)`)
      }
      return t('plants.hintPlace', `Click on canvas to place ${selectedProduct.name}`)
    }
    return t('plants.hintDefault', 'Select a product, then click on canvas to place')
  }
}
